import { memo, useCallback, useMemo, type ReactNode } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    OutlinedInput,
    type SelectChangeEvent,
} from '@mui/material';
import { ProgramTypeConfigs } from 'Program';
import { ProgramType } from 'Program/Program.types';
import { InstitutionId } from 'Institutions';

/**
 * Special value used by the backend to represent all ExecEd certificate types.
 * The backend aggregates all exec_ed certificate types into this single value.
 */
const CERTIFICATES_VALUE = 'certificates';

/**
 * Program types grouped by institution for the filter dropdown.
 * Uses the actual ProgramType enum values to ensure correctness.
 */
const PROGRAM_TYPE_GROUPS: { label: string; types: (ProgramType | typeof CERTIFICATES_VALUE)[] }[] = [
    {
        label: 'Quantic',
        types: [ProgramType.emba, ProgramType.mba, ProgramType.msba, ProgramType.msse, ProgramType.msaie],
    },
    {
        label: 'Valar',
        types: [ProgramType.emba_strategic_leadership, ProgramType.mba_leadership],
    },
    {
        label: 'Exec Ed Certs',
        types: [CERTIFICATES_VALUE],
    },
];

/**
 * Get the human-readable label for a program type.
 * Uses fullTitle from ProgramTypeConfigs for clarity.
 */
function getProgramTypeLabel(type: ProgramType | string): string {
    // Handle the special "certificates" aggregate value
    if (type === CERTIFICATES_VALUE) return 'Exec Ed Certificates';

    const config = ProgramTypeConfigs[type as ProgramType];
    if (!config) return type;

    // For degree programs, include institution prefix if Valar
    const institutionPrefix = config.institutionID === InstitutionId.valar ? 'Valar ' : '';
    return `${institutionPrefix}${config.fullTitle(null)}`;
}

// Styling to suppress .front-royal-form-container border overrides
const FILTER_SX = {
    minWidth: 150,
    maxWidth: 400,
    '& .MuiInputBase-root': {
        minHeight: 40,
        height: 'auto', // Allow height to grow for chip wrapping
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)' },
    '& input, & select': { border: 'none !important', boxShadow: 'none !important' },
    // Fix floating label positioning when shrunk
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(8px, -9px) scale(0.75)',
        transformOrigin: 'top left',
    },
    // Ensure chips have room to wrap
    '& .MuiSelect-select': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
    },
};

interface ProgramTypeFilterSelectMultipleProps {
    /** Current selected values */
    value: string[];
    /** Called when selection changes */
    onChange: (value: string[]) => void;
    /** Multiple selection mode */
    multiple: true;
    /** Label for the select */
    label?: string;
    /** Size variant */
    size?: 'small' | 'medium';
    /** Whether to show "All" option (only for single select) */
    showAllOption?: never;
}

interface ProgramTypeFilterSelectSingleProps {
    /** Current selected value (empty string for "All") */
    value: string;
    /** Called when selection changes */
    onChange: (value: string) => void;
    /** Single selection mode */
    multiple?: false;
    /** Label for the select */
    label?: string;
    /** Size variant */
    size?: 'small' | 'medium';
    /** Whether to show "All" option */
    showAllOption?: boolean;
}

type ProgramTypeFilterSelectProps = ProgramTypeFilterSelectMultipleProps | ProgramTypeFilterSelectSingleProps;

// Chip delete handlers are passed via closure to avoid event propagation issues
function createChipDeleteHandler(value: string, currentValues: string[], onChange: (values: string[]) => void) {
    return (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        onChange(currentValues.filter(v => v !== value));
    };
}

function MultipleValueRenderer({ selected, onChange }: { selected: string[]; onChange: (values: string[]) => void }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
            {selected.map(value => {
                const label = getProgramTypeLabel(value);
                return (
                    <Chip
                        key={value}
                        label={label}
                        size="small"
                        onDelete={createChipDeleteHandler(value, selected, onChange)}
                        onMouseDown={event => event.stopPropagation()}
                    />
                );
            })}
        </Box>
    );
}

function renderSingleValue(selected: string) {
    if (!selected) return 'All Programs';
    const label = getProgramTypeLabel(selected);
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip label={label} size="small" />
        </Box>
    );
}

function renderMenuItems(showAllOption: boolean): ReactNode[] {
    const items: ReactNode[] = [];

    if (showAllOption) {
        items.push(
            <MenuItem key="all" value="">
                All Programs
            </MenuItem>,
        );
    }

    PROGRAM_TYPE_GROUPS.forEach(group => {
        // Add group header
        items.push(
            <MenuItem key={`header-${group.label}`} disabled sx={{ fontWeight: 'bold', opacity: 1 }}>
                {group.label}
            </MenuItem>,
        );

        // Add program types in this group
        group.types.forEach(type => {
            const label = getProgramTypeLabel(type);
            items.push(
                <MenuItem key={type} value={type} sx={{ pl: 4 }}>
                    {label}
                </MenuItem>,
            );
        });
    });

    return items;
}

function ProgramTypeFilterSelectComponent(props: ProgramTypeFilterSelectProps) {
    const { label = 'Program Type', size = 'small', multiple = false } = props;

    const handleMultipleChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const newValue = event.target.value;
            (props as ProgramTypeFilterSelectMultipleProps).onChange(
                typeof newValue === 'string' ? newValue.split(',') : newValue,
            );
        },
        [props],
    );

    const handleSingleChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            (props as ProgramTypeFilterSelectSingleProps).onChange(event.target.value);
        },
        [props],
    );

    const showAllOption = !multiple && (props as ProgramTypeFilterSelectSingleProps).showAllOption !== false;
    const menuItems = useMemo(() => renderMenuItems(showAllOption), [showAllOption]);

    if (multiple) {
        const multipleProps = props as ProgramTypeFilterSelectMultipleProps;
        return (
            <FormControl sx={FILTER_SX} size={size}>
                <InputLabel>{label}</InputLabel>
                <Select
                    multiple
                    value={multipleProps.value}
                    onChange={handleMultipleChange}
                    input={<OutlinedInput label={label} />}
                    renderValue={selected => (
                        <MultipleValueRenderer selected={selected} onChange={multipleProps.onChange} />
                    )}
                >
                    {menuItems}
                </Select>
            </FormControl>
        );
    }

    const singleProps = props as ProgramTypeFilterSelectSingleProps;
    return (
        <FormControl sx={FILTER_SX} size={size}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={singleProps.value}
                onChange={handleSingleChange}
                label={label}
                renderValue={renderSingleValue}
            >
                {menuItems}
            </Select>
        </FormControl>
    );
}

export const ProgramTypeFilterSelect = memo(ProgramTypeFilterSelectComponent);
export { getProgramTypeLabel, PROGRAM_TYPE_GROUPS };
